import type { SyntheticEvent } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { track } from "../../lib/events";
import { WatchSession } from "../../lib/watchSession";
import type { ContentItem } from "../../types";

type MediaEvent = SyntheticEvent<HTMLMediaElement>;

// Connects a <video> or <audio> element to a WatchSession: one per visit to the
// item, started by the first play. Leaving the item, closing the page or
// hiding the app each leave the latest totals behind.
export function useWatchSession(
  item: ContentItem,
  moduleID: string,
  mediaSrc: string | undefined,
  options: { itemUniqueSeconds?: () => number; previouslyWatched?: () => ReadonlySet<number> } = {}
) {
  const { i18n } = useTranslation();
  const session = useRef<WatchSession | null>(null);
  // Handlers read the latest values without being re-created on every render.
  const latest = useRef({ mediaSrc, language: i18n.language, options });
  useEffect(() => {
    latest.current = { mediaSrc, language: i18n.language, options };
  });

  function start(): WatchSession {
    if (!session.current || session.current.finished) {
      session.current = new WatchSession(
        {
          emit: (type, payload) => track(type, { module_id: moduleID, item_id: item.id, payload }),
          context: () => ({
            source: latest.current.mediaSrc?.startsWith("blob:") ? "offline_copy" : "online",
            ui_language: latest.current.language,
          }),
          itemUniqueSeconds: latest.current.options.itemUniqueSeconds,
        },
        latest.current.options.previouslyWatched?.()
      );
    }
    return session.current;
  }

  useEffect(() => {
    const hidden = () => {
      if (document.visibilityState === "hidden") session.current?.checkpoint();
    };
    const leave = () => session.current?.exit();
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", leave);
    return () => {
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", leave);
      leave();
      session.current = null;
    };
  }, [item.id]);

  return {
    onPlay: (event: MediaEvent) => {
      const element = event.currentTarget;
      start().play(element.currentTime, element.duration || 0, element.playbackRate);
    },
    onPause: (event: MediaEvent) => {
      const element = event.currentTarget;
      session.current?.pause(element.currentTime, element.duration || 0, element.ended);
    },
    onTimeUpdate: (event: MediaEvent) => {
      const element = event.currentTarget;
      session.current?.timeUpdate(element.currentTime, element.duration || 0, element.seeking);
    },
    onSeeking: () => session.current?.seeking(),
    onSeeked: (event: MediaEvent) => session.current?.seeked(event.currentTarget.currentTime),
    onEnded: (event: MediaEvent) => {
      const element = event.currentTarget;
      session.current?.ended(element.currentTime, element.duration || 0);
    },
  };
}
